import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Truck, CheckCircle, Clock, FileText, AlertCircle, Search, CreditCard, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusHistoryLabels = {
    pending: 'Поръчката е приета',
    pending_payment: 'Очаква плащане',
    awaiting_prepayment: 'Очаква предварително плащане',
    confirmed: 'Платена',
    processing: 'Обработва се',
    shipped: 'Изпратена',
    delivered: 'Доставена',
    cancelled: 'Отказана',
    refunded: 'Върната',
    payment_failed: 'Неуспешно плащане'
};

const paymentStatusLabels = {
    pending: 'Очаква плащане',
    paid: 'Платена',
    cod: 'Наложен платеж — при доставка',
    failed: 'Плащането е неуспешно',
    awaiting_transfer: 'Очаква банков превод',
    refunded: 'Възстановена сума'
};

const paymentMethodLabels = {
    cod: 'Наложен платеж',
    card: 'Карта (MyPOS)',
    bank_transfer: 'Банков превод'
};

const OrderTrackingPage = () => {
    const { orderId: urlOrderId } = useParams();
    const [searchParams] = useSearchParams();

    const initialContact = searchParams.get('contact') || searchParams.get('email') || searchParams.get('phone') || '';

    const [orderId, setOrderId] = useState(urlOrderId || '');
    const [contact, setContact] = useState(initialContact);
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    useEffect(() => {
        if (urlOrderId && urlOrderId !== orderId) {
            setOrderId(urlOrderId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlOrderId]);

    useEffect(() => {
        if (urlOrderId && urlOrderId.startsWith('ord_') && initialContact) {
            doTrack(urlOrderId, initialContact);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlOrderId]);

    const doTrack = async (oid, cnt) => {
        setLoading(true);
        setError('');
        setSearched(true);
        try {
            const response = await axios.get(`${API}/orders/track/${oid.trim()}`, {
                params: { contact: cnt.trim() }
            });
            setOrder(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Поръчката не е намерена');
            setOrder(null);
        } finally {
            setLoading(false);
        }
    };

    const handleTrack = async (e) => {
        if (e) e.preventDefault();
        if (!orderId.trim()) {
            setError('Моля, въведете номер на поръчка');
            return;
        }
        if (!contact.trim()) {
            setError('Моля, въведете телефона или имейла, с който сте направили поръчката');
            return;
        }
        doTrack(orderId, contact);
    };

    const getStatusStep = (status) => {
        const steps = {
            pending: 1, pending_payment: 1, awaiting_prepayment: 1,
            confirmed: 2, processing: 2,
            shipped: 3,
            delivered: 4,
            cancelled: 0, refunded: 0, payment_failed: 0
        };
        return steps[status] || 1;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('bg-BG', {
            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white dark:from-slate-900 dark:to-slate-800 py-12">
            <div className="container mx-auto px-4 max-w-3xl">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                        Проследяване на поръчка
                    </h1>
                    <p className="text-slate-600 dark:text-slate-400">
                        Въведете номера на поръчката и телефона или имейла, с който сте я направили
                    </p>
                </div>

                {/* Search Form */}
                <form onSubmit={handleTrack} className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 mb-8">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <Label htmlFor="orderId">Номер на поръчка *</Label>
                            <Input
                                id="orderId"
                                value={orderId}
                                onChange={(e) => setOrderId(e.target.value)}
                                placeholder="напр. ord_abc123..."
                                className="mt-1"
                                required
                                data-testid="track-order-id"
                            />
                        </div>
                        <div>
                            <Label htmlFor="contact">Телефон или имейл *</Label>
                            <Input
                                id="contact"
                                value={contact}
                                onChange={(e) => setContact(e.target.value)}
                                placeholder="0899123456 или вашият@имейл.com"
                                className="mt-1"
                                required
                                data-testid="track-contact"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                        🔒 За защита на личните ви данни се изисква телефонът или имейлът, използван при поръчката.
                    </p>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-amber-500 hover:bg-amber-600"
                        data-testid="track-submit"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                Зареждане...
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <Search className="w-4 h-4" />
                                Проследи поръчка
                            </span>
                        )}
                    </Button>
                </form>

                {/* Error */}
                {error && searched && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-8 flex items-center gap-3" data-testid="track-error">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-red-700 dark:text-red-400">{error}</p>
                    </div>
                )}

                {/* Order Details */}
                {order && (
                    <div className="space-y-6" data-testid="order-tracking-result">
                        {/* Status Progress */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                                    Поръчка #{order.order_id?.slice(0, 16).toUpperCase()}
                                </h2>
                                <span className="inline-block bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-3 py-1 rounded-full text-sm font-medium" data-testid="track-status-label">
                                    {order.status_display?.bg || statusHistoryLabels[order.status] || order.status}
                                </span>
                            </div>

                            {/* Progress Steps */}
                            <div className="relative">
                                <div className="flex justify-between items-center">
                                    {['Приета', 'Обработва се', 'Изпратена', 'Доставена'].map((step, idx) => {
                                        const stepNum = idx + 1;
                                        const currentStep = getStatusStep(order.status);
                                        const isActive = stepNum <= currentStep;
                                        const isCurrent = stepNum === currentStep;

                                        return (
                                            <div key={step} className="flex flex-col items-center relative z-10">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                    isActive
                                                        ? 'bg-amber-500 text-white'
                                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                                                } ${isCurrent ? 'ring-4 ring-amber-200 dark:ring-amber-900' : ''}`}>
                                                    {stepNum === 1 && <Clock className="w-5 h-5" />}
                                                    {stepNum === 2 && <Package className="w-5 h-5" />}
                                                    {stepNum === 3 && <Truck className="w-5 h-5" />}
                                                    {stepNum === 4 && <CheckCircle className="w-5 h-5" />}
                                                </div>
                                                <span className={`mt-2 text-xs font-medium ${
                                                    isActive ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'
                                                }`}>
                                                    {step}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700 -z-0">
                                    <div
                                        className="h-full bg-amber-500 transition-all duration-500"
                                        style={{ width: `${Math.max(getStatusStep(order.status) - 1, 0) * 33.33}%` }}
                                    />
                                </div>
                            </div>

                            {/* Cancelled/Refunded Status */}
                            {['cancelled', 'refunded', 'payment_failed'].includes(order.status) && (
                                <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                    <p className="text-red-700 dark:text-red-400 font-medium">
                                        {order.status === 'cancelled' && '❌ Поръчката е отказана'}
                                        {order.status === 'refunded' && '💰 Сумата е възстановена'}
                                        {order.status === 'payment_failed' && '⚠️ Плащането е неуспешно'}
                                    </p>
                                </div>
                            )}

                            {/* Payment info */}
                            <div className="mt-6 flex flex-wrap gap-4 text-sm border-t border-gray-100 dark:border-gray-700 pt-4">
                                <div className="flex items-center gap-2">
                                    <CreditCard className="w-4 h-4 text-amber-500" />
                                    <span className="text-gray-500">Плащане:</span>
                                    <span className="font-medium">{paymentMethodLabels[order.payment_method] || order.payment_method}</span>
                                </div>
                                <div>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        order.payment_status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                        : order.payment_status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                    }`} data-testid="track-payment-status">
                                        {paymentStatusLabels[order.payment_status] || order.payment_status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Status History */}
                        {order.status_history?.length > 0 && (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6" data-testid="track-status-history">
                                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-white">
                                    <History className="w-5 h-5 text-amber-500" />
                                    История на поръчката
                                </h3>
                                <div className="space-y-3">
                                    {[...order.status_history].reverse().map((entry, idx) => (
                                        <div key={idx} className="flex items-start gap-3">
                                            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${idx === 0 ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                                            <div>
                                                <p className={`text-sm ${idx === 0 ? 'font-semibold text-slate-800 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                                                    {statusHistoryLabels[entry.status] || entry.status}
                                                    {entry.note ? ` — ${entry.note}` : ''}
                                                </p>
                                                <p className="text-xs text-gray-400">{formatDate(entry.changed_at)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Econt Tracking */}
                        {order.econt_shipment && (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
                                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-white">
                                    <Truck className="w-5 h-5 text-amber-500" />
                                    Доставка с Еконт
                                </h3>
                                <div className="space-y-2">
                                    <p className="text-sm">
                                        <span className="text-gray-500">Товарителница:</span>{' '}
                                        <span className="font-mono font-medium">{order.econt_shipment.shipment_number}</span>
                                    </p>
                                    <p className="text-sm">
                                        <span className="text-gray-500">Цена доставка:</span>{' '}
                                        <span className="font-medium">{order.econt_shipment.total_price} {order.econt_shipment.currency}</span>
                                    </p>
                                    {order.econt_tracking?.success && (
                                        <p className="text-sm">
                                            <span className="text-gray-500">Текущ статус:</span>{' '}
                                            <span className="font-medium text-amber-600">{order.econt_tracking.status}</span>
                                        </p>
                                    )}
                                    <a
                                        href={`https://www.econt.com/services/track-shipment/?shipmentNumber=${order.econt_shipment.shipment_number}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 mt-2 text-amber-600 hover:text-amber-700 text-sm font-medium"
                                        data-testid="track-econt-link"
                                    >
                                        Проследи в Econt.com →
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Order Info */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
                            <h3 className="font-semibold mb-4 text-slate-800 dark:text-white">Детайли на поръчката</h3>

                            {/* Items */}
                            <div className="space-y-3 mb-4">
                                {order.items?.map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                                        <div className="flex items-center gap-3">
                                            {item.image_url && (
                                                <img src={item.image_url} alt={item.name} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
                                            )}
                                            <div>
                                                <p className="font-medium text-slate-800 dark:text-white text-sm">{item.name}</p>
                                                <p className="text-sm text-gray-500">{item.quantity} x {item.price?.toFixed(2)} €</p>
                                            </div>
                                        </div>
                                        <p className="font-medium">{(item.price * item.quantity).toFixed(2)} €</p>
                                    </div>
                                ))}
                            </div>

                            {/* Totals */}
                            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">
                                        {order.delivery_method === 'sofia_driver' ? 'Доставка в гр. София с наш шофьор' : 'Доставка'}
                                    </span>
                                    <span className={order.shipping_cost > 0 ? '' : 'text-green-600 font-medium'}>
                                        {order.shipping_cost > 0 ? `${order.shipping_cost?.toFixed(2)} €` : 'Безплатна'}
                                    </span>
                                </div>
                                <div className="flex justify-between font-bold text-lg">
                                    <span>Общо</span>
                                    <span className="text-amber-600">{order.total?.toFixed(2)} €</span>
                                </div>
                            </div>

                            {/* Shipping Address */}
                            {order.shipping_address && (
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <p className="text-sm text-gray-500 mb-1">Доставка до:</p>
                                    <p className="font-medium">{order.shipping_address.name}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {[order.shipping_address.address, order.shipping_address.city].filter(Boolean).join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Invoice */}
                            {order.invoice_number && (
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-gray-500" />
                                    <span className="text-sm text-gray-500">Фактура: #{order.invoice_number}</span>
                                </div>
                            )}

                            {/* Date */}
                            <p className="text-xs text-gray-400 mt-4">
                                Поръчано на: {formatDate(order.created_at)}
                            </p>
                        </div>

                        {/* Back to shop */}
                        <div className="text-center">
                            <Link to="/products">
                                <Button variant="outline">
                                    ← Продължи пазаруването
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderTrackingPage;
